import { ChangeEvent, MouseEvent, useEffect, useState } from 'react';

import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, Chip, Collapse, Divider, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Checkbox } from '@sage/ui-components';

import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';
import { ObservationDescriptor } from 'src/constants/ObservationTypes';

export interface ObservationDetails {
    selectedDescriptors: string[];
    note: string;
}

// enforced by the DB, so don't increase this :)
export const NOTE_CHARACTER_LIMIT = 255;

export const EMPTY_DETAILS: ObservationDetails = { selectedDescriptors: [], note: '' };

interface BaseObservationOptionCardProps {
    id: string;
    label: string;
    icon: IconDefinition;
    isSelected: boolean;
    onSelect: (id: string, isSelected: boolean) => void;
    descriptors?: ObservationDescriptor[];
    analyticsPrefix: string;
}

type DetailsProps =
    | {
          details: ObservationDetails;
          onDetailsChange: (id: string, details: ObservationDetails) => void;
      }
    | { details?: undefined; onDetailsChange?: undefined };

export type ObservationOptionCardProps = BaseObservationOptionCardProps & DetailsProps;

function ObservationOptionCard(props: ObservationOptionCardProps) {
    const {
        id,
        label,
        icon,
        isSelected,
        onSelect,
        descriptors,
        details = EMPTY_DETAILS,
        onDetailsChange,
        analyticsPrefix,
    } = props;

    const { palette, spacing } = useTheme();
    const { trackEvent } = useAnalytics();

    const hasDescriptors = !!descriptors?.length;

    const { selectedDescriptors } = details;
    const [note, setNote] = useState(details.note);

    const isOverCharacterLimit = note.length > NOTE_CHARACTER_LIMIT;
    const charDifference = Math.abs(note.length - NOTE_CHARACTER_LIMIT);

    const handleSelect = () => {
        onSelect(id, !isSelected);
    };

    const handleDescriptorClick = (e: MouseEvent, descriptorValue: string) => {
        const isAlreadySelected = selectedDescriptors.includes(descriptorValue);
        const nextSelectedDescriptors = isAlreadySelected
            ? selectedDescriptors.filter((s) => s !== descriptorValue)
            : [...selectedDescriptors, descriptorValue];

        onDetailsChange?.(id, { selectedDescriptors: nextSelectedDescriptors, note });

        trackEvent('Observation Detail Clicked', {
            observationDetail: descriptorValue,
            isChecked: !isAlreadySelected,
            context: analyticsPrefix,
            objectType: ObjectTypes.Button,
        });
    };

    const handleNoteChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setNote(e.target.value);
    };

    const handleNoteBlur = () => {
        onDetailsChange?.(id, { selectedDescriptors, note });
    };

    const handleNoteFocus = () => {
        trackEvent('Notes Focused', {
            observationId: id,
            context: analyticsPrefix,
            objectType: ObjectTypes.Input,
        });
    };

    useEffect(() => {
        // we have to proactively clear any local note state if the obs becomes
        // unchecked
        if (!isSelected) {
            setNote('');
        }
    }, [isSelected]);

    return (
        <Card
            onClick={handleSelect}
            data-testid={`observation-option-${id}`}
            aria-pressed={isSelected}
            sx={{
                borderRadius: 3,
                boxShadow: 0,
                overflow: 'hidden',
                outline: isSelected
                    ? `2px solid ${palette.orange[500]}`
                    : `1px solid ${palette.grey[300]}`,
            }}
        >
            <Stack direction="row" sx={{ alignItems: 'center', height: spacing(9.5) }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        alignSelf: 'stretch',
                        minWidth: spacing(8),
                        px: 2,
                        bgcolor: 'orange.100',
                    }}
                >
                    <FontAwesomeIcon
                        icon={icon}
                        color={palette.orange[500]}
                        style={{ width: 40, height: 40 }}
                    />
                </Box>
                <Typography variant="body1" sx={{ flex: 1, px: 2, fontWeight: 'bold' }}>
                    {label}
                </Typography>
                <Checkbox
                    checked={isSelected}
                    onClick={handleSelect}
                    colorVariant="warning"
                    sx={{ mr: 2 }}
                />
            </Stack>
            <Collapse in={isSelected} unmountOnExit>
                <Divider sx={{ borderColor: palette.grey[200] }} />
                <Box onClick={(e: MouseEvent) => e.stopPropagation()} sx={{ p: 2 }}>
                    {hasDescriptors && (
                        <>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1.25 }}>
                                Details{' '}
                                <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{ color: palette.grey[700] }}
                                >
                                    (optional)
                                </Typography>
                            </Typography>
                            <Stack
                                direction="row"
                                sx={{ columnGap: 1, rowGap: 1.25, flexWrap: 'wrap' }}
                                role="group"
                                aria-label={`${label} details`}
                            >
                                {descriptors!.map((descriptor) => {
                                    const isChipSelected = selectedDescriptors.includes(
                                        descriptor.value
                                    );
                                    const chipBgColor = isChipSelected
                                        ? palette.orange[100]
                                        : palette.white.main;
                                    return (
                                        <Chip
                                            key={descriptor.value}
                                            clickable
                                            onClick={(e: MouseEvent) =>
                                                handleDescriptorClick(e, descriptor.value)
                                            }
                                            variant="outlined"
                                            aria-pressed={isChipSelected}
                                            data-testid={`observation-descriptor-${id}-${descriptor.value}`}
                                            label={descriptor.label}
                                            sx={{
                                                // @sage/ui-components forces height/radius/padding
                                                // via && — we have to match it's specificity in order to override
                                                '&&': {
                                                    height: spacing(5.5),
                                                    minHeight: spacing(5.5),
                                                    borderRadius: spacing(3.5),
                                                    paddingInline: 2,
                                                    outline: isChipSelected
                                                        ? `2px solid ${palette.orange[500]}`
                                                        : 'none',
                                                    backgroundColor: chipBgColor,
                                                    // have to apply to each of these pseudo-classes/classes
                                                    // or MUI will apply it's own styles.
                                                    '&:hover, &:focus, &:active, &.Mui-focusVisible':
                                                        { backgroundColor: chipBgColor },
                                                    outlineOffset: '-1px',
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        </>
                    )}
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 'bold',
                            mb: 1.25,
                            mt: hasDescriptors ? 2 : 0,
                        }}
                    >
                        Note{' '}
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{ color: palette.grey[700] }}
                        >
                            (optional)
                        </Typography>
                    </Typography>
                    <TextField
                        value={note}
                        onChange={handleNoteChange}
                        onBlur={handleNoteBlur}
                        onFocus={handleNoteFocus}
                        placeholder="Enter note here"
                        fullWidth
                        multiline
                        minRows={1}
                        error={isOverCharacterLimit}
                        helperText={
                            isOverCharacterLimit
                                ? `${charDifference} characters over the limit`
                                : `${charDifference} characters remaining`
                        }
                    />
                </Box>
            </Collapse>
        </Card>
    );
}

export default ObservationOptionCard;
